import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Upload, 
  BarChart3, 
  Brain, 
  Download, 
  ArrowRight, 
  CheckCircle2,
  Zap,
  Shield,
  TrendingUp,
  Check,
  Building2,
  Rocket,
  FileText,
  DollarSign,
  ChevronDown
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import axios from "axios";
import { API } from "../App";

const Landing = () => {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/pricing`);
        setTiers(response.data.tiers);
      } catch (error) {
        setTiers([
          { id: "free", name: "Free", price: 0, features: ["3 comparisons per month", "Up to 3 vendors", "Basic matching", "CSV export"] },
          { id: "standard", name: "Standard", price: 29, features: ["20 comparisons per month", "Up to 10 vendors", "AI analysis", "Excel & CSV export"] },
          { id: "pro", name: "Pro", price: 79, features: ["Unlimited comparisons", "Unlimited vendors", "Advanced AI", "Priority support"] }
        ]);
      }
    };
    fetchPricing();
  }, []);

  const features = [
    {
      icon: <Upload className="w-8 h-8" />,
      title: "Drag & Drop Upload",
      description: "Upload vendor quotes in PDF, Excel, Word, or image formats. Our AI extracts all line items automatically."
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI-Powered Analysis",
      description: "Get intelligent insights on scope, terms, payment conditions, and delivery. See pros, cons, and recommendation scores."
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Side-by-Side Comparison",
      description: "View all vendors in a unified table. Lowest prices highlighted automatically. Never miss a better deal."
    },
    {
      icon: <Download className="w-8 h-8" />,
      title: "Export to Excel",
      description: "Download your analysis as Excel or CSV. Share with stakeholders or keep for records."
    }
  ];

  const stats = [
    { value: "50%", label: "Time Saved" },
    { value: "3x", label: "Faster Decisions" },
    { value: "15%", label: "Average Savings" }
  ];

  const tierIcons = {
    free: <Zap className="w-7 h-7" />,
    standard: <Building2 className="w-7 h-7" />,
    pro: <Rocket className="w-7 h-7" />
  };

  const tierColors = {
    free: "border-gray-500/30 hover:border-gray-400",
    standard: "border-primary-main/50 hover:border-primary-main ring-1 ring-primary-main/20",
    pro: "border-secondary-main/50 hover:border-secondary-main"
  };

  return (
    <div className="min-h-screen bg-[#020408] grid-bg">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute top-20 left-10 w-96 h-96 bg-primary-main/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-secondary-main/10 rounded-full blur-[100px]" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-main/10 border border-primary-main/30 rounded-full">
                <Zap className="w-4 h-4 text-primary-main" />
                <span className="text-sm font-mono text-primary-main uppercase tracking-wider">
                  AI-Powered Analysis
                </span>
              </div>
              
              <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter uppercase leading-tight">
                <span className="text-white">Compare</span>
                <br />
                <span className="text-primary-main neon-cyan">Vendors</span>
                <br />
                <span className="text-white">Smarter</span>
              </h1>
              
              <p className="text-lg text-gray-300 leading-relaxed max-w-lg">
                Upload vendor quotes, let AI analyze terms and pricing, compare side-by-side, 
                and make data-driven procurement decisions in minutes.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  data-testid="hero-get-started-btn"
                  onClick={() => navigate("/signup")}
                  className="btn-skew bg-primary-main text-black font-bold uppercase tracking-wider px-8 py-6 hover:bg-primary-light hover:shadow-glow-cyan transition-all"
                >
                  <span>Start Free Trial</span>
                </Button>
                <Button 
                  data-testid="hero-demo-btn"
                  variant="outline"
                  onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
                  className="btn-skew border-primary-main text-primary-main font-bold uppercase tracking-wider px-8 py-6 hover:bg-primary-main/10 transition-all"
                >
                  <span>See How It Works</span>
                </Button>
              </div>
            </div>
            
            {/* Hero Visual */}
            <div className="relative">
              <div className="glass rounded-xl p-6 animate-fade-in">
                <div className="aspect-video bg-background-paper rounded-lg overflow-hidden relative">
                  <img 
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800" 
                    alt="Analytics Dashboard"
                    className="w-full h-full object-cover opacity-60"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-paper via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-accent-success animate-pulse" />
                      <span className="text-sm font-mono text-gray-300">AI Analysis Active</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-4 -right-4 glass p-4 rounded-lg animate-slide-in-left" style={{ animationDelay: '0.2s' }}>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-accent-success" />
                  <span className="font-mono text-sm">+15% Savings</span>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 glass p-4 rounded-lg animate-slide-in-left" style={{ animationDelay: '0.4s' }}>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary-main" />
                  <span className="font-mono text-sm">3 Vendors Analyzed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 border-y border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-display text-5xl md:text-6xl font-bold text-primary-main neon-cyan">
                  {stat.value}
                </div>
                <div className="text-gray-400 uppercase tracking-wider text-sm mt-2">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono text-primary-main uppercase tracking-widest text-sm">Features</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
              Everything You Need
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group p-8 bg-gradient-to-b from-white/5 to-transparent border border-white/5 hover:border-primary-main/50 transition-all duration-500 rounded-xl"
              >
                <div className="w-14 h-14 rounded-lg bg-primary-main/10 flex items-center justify-center text-primary-main mb-6 group-hover:shadow-glow-cyan transition-all">
                  {feature.icon}
                </div>
                <h3 className="font-display text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo/Screenshots Section */}
      <section id="demo" className="py-24 px-6 bg-background-paper/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono text-primary-main uppercase tracking-widest text-sm">How It Works</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
              See the AI in Action
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              Watch how VendorCompare transforms complex vendor quotes into clear, actionable insights
            </p>
          </div>
          
          {/* Step 1: Upload */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div className="order-2 md:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-main/10 rounded-full mb-4">
                <span className="font-display text-2xl font-bold text-primary-main">01</span>
                <span className="text-sm text-primary-main uppercase tracking-wider">Upload</span>
              </div>
              <h3 className="font-display text-3xl font-bold mb-4">Drag & Drop Your Vendor Quotes</h3>
              <p className="text-gray-400 mb-6">
                Simply drag and drop vendor documents in any format - PDF, Excel, Word, or even images. 
                Our AI automatically extracts all line items, pricing, and terms.
              </p>
              <ul className="space-y-3">
                {["Supports PDF, Excel, CSV, Word, Images", "Auto-extracts line items & prices", "Handles multiple vendors at once"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-primary-main" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 md:order-2">
              <div className="glass rounded-xl p-6">
                <div className="border-2 border-dashed border-primary-main/30 rounded-lg p-8 text-center bg-background-paper/50">
                  <Upload className="w-16 h-16 text-primary-main/50 mx-auto mb-4" />
                  <p className="text-lg text-gray-300 mb-2">Drag vendor files here</p>
                  <p className="text-sm text-gray-500">PDF, Excel, CSV, Word, Images</p>
                  <div className="mt-6 flex flex-wrap gap-2 justify-center">
                    <span className="px-3 py-1 bg-red-500/10 text-red-400 rounded text-xs">acme_quote.pdf</span>
                    <span className="px-3 py-1 bg-green-500/10 text-green-400 rounded text-xs">global_pricing.xlsx</span>
                    <span className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded text-xs">premium_offer.docx</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2: AI Analysis */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <div className="glass rounded-xl p-6">
                {/* AI Analysis Card Mock */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Brain className="w-6 h-6 text-primary-main animate-pulse" />
                      <span className="font-semibold">AI Analysis</span>
                    </div>
                    <span className="px-2 py-1 bg-accent-success/20 text-accent-success text-xs rounded">Processing</span>
                  </div>
                  
                  {/* Vendor Card Mock */}
                  <div className="bg-background-subtle rounded-lg p-4 border border-border">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold">Acme Corp</h4>
                        <p className="text-xs text-gray-500">acme_quote.pdf</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary-main">85<span className="text-sm text-gray-400">/100</span></div>
                        <div className="text-xs text-gray-500">AI Score</div>
                      </div>
                    </div>
                    <div className="h-2 bg-background rounded-full overflow-hidden mb-3">
                      <div className="h-full w-[85%] bg-primary-main rounded-full" />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1 text-accent-success">
                        <Check className="w-3 h-3" /> Competitive pricing
                      </div>
                      <div className="flex items-center gap-1 text-accent-success">
                        <Check className="w-3 h-3" /> Fast delivery
                      </div>
                      <div className="flex items-center gap-1 text-red-400">
                        <span className="w-3 h-3 flex items-center justify-center">!</span> Min order required
                      </div>
                    </div>
                  </div>
                  
                  {/* Analysis Details */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-background-subtle p-3 rounded-lg">
                      <div className="text-gray-500 mb-1">Scope of Work</div>
                      <div className="text-gray-300">Industrial materials supply with quality certification</div>
                    </div>
                    <div className="bg-background-subtle p-3 rounded-lg">
                      <div className="text-gray-500 mb-1">Payment Terms</div>
                      <div className="text-gray-300">Net 30, 2% early payment discount</div>
                    </div>
                    <div className="bg-background-subtle p-3 rounded-lg">
                      <div className="text-gray-500 mb-1">Delivery</div>
                      <div className="text-gray-300">5-7 business days, express available</div>
                    </div>
                    <div className="bg-background-subtle p-3 rounded-lg">
                      <div className="text-gray-500 mb-1">Total Quote</div>
                      <div className="text-primary-main font-bold text-lg">$2,500</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-main/10 rounded-full mb-4">
                <span className="font-display text-2xl font-bold text-primary-main">02</span>
                <span className="text-sm text-primary-main uppercase tracking-wider">Analyze</span>
              </div>
              <h3 className="font-display text-3xl font-bold mb-4">AI Analyzes Everything</h3>
              <p className="text-gray-400 mb-6">
                Our AI reads and understands your vendor documents, extracting key information about 
                scope, terms, conditions, and pricing. It then generates recommendation scores based on 
                multiple factors.
              </p>
              <ul className="space-y-3">
                {["Scope of work analysis", "Payment & delivery terms extraction", "Pros & cons identification", "Recommendation scores (0-100)"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-primary-main" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Step 3: Compare */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-main/10 rounded-full mb-4">
                <span className="font-display text-2xl font-bold text-primary-main">03</span>
                <span className="text-sm text-primary-main uppercase tracking-wider">Compare</span>
              </div>
              <h3 className="font-display text-3xl font-bold mb-4">Side-by-Side Comparison</h3>
              <p className="text-gray-400 mb-6">
                See all vendors in one unified table. Lowest prices are highlighted automatically. 
                Use checkboxes to cherry-pick the best items from each vendor and see your total savings.
              </p>
              <ul className="space-y-3">
                {["Unified comparison table", "Lowest price highlighting", "Cherry-pick best items per vendor", "Real-time total calculation", "Export to Excel with one click"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-primary-main" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 md:order-2">
              <div className="glass rounded-xl p-6 overflow-hidden">
                {/* Comparison Table Mock */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-subtle text-gray-400 text-xs uppercase">
                        <th className="py-2 px-3 text-left">Item</th>
                        <th className="py-2 px-3 text-left">Qty</th>
                        <th className="py-2 px-3 text-left">Acme</th>
                        <th className="py-2 px-3 text-left">Global</th>
                        <th className="py-2 px-3 text-left">Premium</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-border">
                        <td className="py-3 px-3 font-medium">Steel Plates</td>
                        <td className="py-3 px-3 text-gray-400">100</td>
                        <td className="py-3 px-3">$15</td>
                        <td className="py-3 px-3 bg-accent-success/10 text-accent-success font-bold">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded border-2 border-accent-success bg-accent-success/20" />
                            $14 <span className="text-[10px] bg-accent-success/20 px-1 rounded">LOW</span>
                          </div>
                        </td>
                        <td className="py-3 px-3">$18</td>
                      </tr>
                      <tr className="border-b border-border">
                        <td className="py-3 px-3 font-medium">Copper Wires</td>
                        <td className="py-3 px-3 text-gray-400">50</td>
                        <td className="py-3 px-3">$12</td>
                        <td className="py-3 px-3">$14</td>
                        <td className="py-3 px-3 bg-accent-success/10 text-accent-success font-bold">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded border-2 border-accent-success bg-accent-success/20" />
                            $10 <span className="text-[10px] bg-accent-success/20 px-1 rounded">LOW</span>
                          </div>
                        </td>
                      </tr>
                      <tr className="border-b border-border">
                        <td className="py-3 px-3 font-medium">Aluminum</td>
                        <td className="py-3 px-3 text-gray-400">25</td>
                        <td className="py-3 px-3">$16</td>
                        <td className="py-3 px-3 bg-accent-success/10 text-accent-success font-bold">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded border-2 border-accent-success bg-accent-success/20" />
                            $8 <span className="text-[10px] bg-accent-success/20 px-1 rounded">LOW</span>
                          </div>
                        </td>
                        <td className="py-3 px-3">$20</td>
                      </tr>
                      <tr className="bg-primary-main/10">
                        <td colSpan={2} className="py-3 px-3 font-bold">SELECTED TOTAL</td>
                        <td colSpan={3} className="py-3 px-3 text-right">
                          <span className="text-xl font-bold text-primary-main">$2,100</span>
                          <span className="text-xs text-gray-400 ml-2">saved $400</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono text-primary-main uppercase tracking-widest text-sm">Pricing</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
              Choose Your <span className="text-primary-main">Plan</span>
            </h2>
            <p className="text-xl text-gray-400 mt-6 max-w-2xl mx-auto">
              Start free and scale as your needs grow. All plans include our core comparison features.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {tiers.map((tier, index) => (
              <div 
                key={tier.id}
                data-testid={`pricing-tier-${tier.id}`}
                className={`relative glass rounded-xl p-8 transition-all duration-300 ${tierColors[tier.id]}`}
              >
                {tier.id === "standard" && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-primary-main text-black text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full">
                      Popular
                    </span>
                  </div>
                )}
                
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center mb-6 ${
                  tier.id === "standard" ? "bg-primary-main/20 text-primary-main" :
                  tier.id === "pro" ? "bg-secondary-main/20 text-secondary-main" :
                  "bg-gray-500/20 text-gray-400"
                }`}>
                  {tierIcons[tier.id]}
                </div>
                
                <h3 className="font-display text-2xl font-semibold mb-2">{tier.name}</h3>
                
                <div className="mb-6">
                  <span className="font-display text-5xl font-bold">
                    ${tier.price}
                  </span>
                  <span className="text-gray-400 ml-2">/month</span>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        tier.id === "standard" ? "text-primary-main" :
                        tier.id === "pro" ? "text-secondary-main" :
                        "text-gray-500"
                      }`} />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  data-testid={`select-tier-${tier.id}-btn`}
                  onClick={() => navigate("/signup", { state: { tier: tier.id } })}
                  className={`w-full btn-skew font-bold uppercase tracking-wider py-6 transition-all ${
                    tier.id === "standard" 
                      ? "bg-primary-main text-black hover:bg-primary-light hover:shadow-glow-cyan" 
                      : tier.id === "pro"
                      ? "bg-secondary-main text-black hover:bg-secondary-light hover:shadow-glow-fuchsia"
                      : "bg-gray-700 text-white hover:bg-gray-600"
                  }`}
                >
                  <span>{tier.price === 0 ? "Start Free" : "Subscribe"}</span>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-background-paper/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Ready to Compare <span className="text-primary-main">Smarter</span>?
          </h2>
          <p className="text-xl text-gray-400 mb-10">
            Join traders and procurement teams saving time and money with AI-powered vendor analysis.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              data-testid="cta-get-started-btn"
              onClick={() => navigate("/signup")}
              className="btn-skew bg-primary-main text-black font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-primary-light hover:shadow-glow-cyan transition-all"
            >
              <span>Start Free Trial</span>
            </Button>
            <Button 
              data-testid="cta-pricing-btn"
              variant="outline"
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              className="btn-skew border-primary-main text-primary-main font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-primary-main/10 transition-all"
            >
              <span>View Pricing</span>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
